import { useEffect, useRef, useState } from "preact/hooks";
import type { FieldOf, GroupSubField } from "@fobu/schema";
import { entryFieldVisible } from "@fobu/engine";
import { useFormEnv } from "../../lib/context";
import { Markdown } from "../../lib/markdown";
import { t } from "../../lib/i18n";
import { Control } from "../Control";
import type { FieldProps } from "./types";

type Entry = Record<string, unknown>;

/** Typen, die als fieldset/legend ausgezeichnet werden (wie bei FieldShell). */
const SET_TYPES = new Set(["single_choice", "multiple_choice", "yes_no", "rating"]);

/**
 * Wiederholbare Feldgruppe („Weiteres Kind hinzufügen“). Jeder Eintrag ist ein eigenes fieldset mit
 * Legende „Kind 2“; Fehler werden dem Eintrag und der Unterfrage zugeordnet (params.item/fieldId).
 */
export function GroupField({ field, value, onChange, a11y }: FieldProps<FieldOf<"group">>) {
  const { ctl, tpl } = useFormEnv();
  const stored = Array.isArray(value) ? (value as Entry[]) : [];
  const start = Math.max(1, field.minItems);
  // Ohne gespeicherte Einträge leere Einträge zum Ausfüllen anzeigen (werden erst bei Eingabe gespeichert).
  const entries: Entry[] = stored.length ? stored : Array.from({ length: Math.min(start, field.maxItems) }, () => ({}));
  const issues = ctl.errors[field.id] ?? [];
  const [announce, setAnnounce] = useState("");
  const [focusItem, setFocusItem] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusItem === null) return;
    root.current?.querySelector<HTMLElement>(`[data-item="${focusItem}"] input, [data-item="${focusItem}"] select, [data-item="${focusItem}"] textarea`)?.focus();
    setFocusItem(null);
  }, [focusItem]);

  // Nur auf „keine Antwort“ zurückfallen, wenn alles leer ist und nicht mehr Einträge als zu Beginn sichtbar sind.
  const commit = (next: Entry[]) => onChange(next.some((e) => Object.keys(e).length) || next.length > start ? next : undefined);
  const update = (i: number, subId: string, v: unknown) => {
    const next = entries.map((e, n) => {
      if (n !== i) return e;
      const copy = { ...e };
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) delete copy[subId];
      else copy[subId] = v;
      return copy;
    });
    commit(next);
  };
  const add = () => {
    const next = [...entries, {}];
    onChange(next);
    setFocusItem(next.length - 1);
    setAnnounce(t.groupAdded(field.itemLabel, next.length));
  };
  const remove = (i: number) => {
    const next = entries.filter((_, n) => n !== i);
    commit(next);
    setAnnounce(t.groupRemoved(field.itemLabel, i + 1));
    setFocusItem(Math.max(0, i - 1));
  };
  const issueFor = (i: number, subId: string) => issues.find((x) => x.params?.item === i && x.params?.fieldId === subId);
  const minVisible = Math.max(field.required ? 1 : 0, field.minItems);

  return (
    <div class="fb-group" ref={root}>
      {entries.map((entry, i) => (
        <fieldset key={i} class="fb-group-item" data-item={i}>
          <legend class="fb-group-legend">
            {field.itemLabel} {i + 1}
          </legend>
          {field.fields.map((sub: GroupSubField) => {
            // Logik je Eintrag: Unterfrage nur, wenn ihre Bedingung für diesen Eintrag erfüllt ist.
            if (!entryFieldVisible(sub, entry, { answers: ctl.flow.answers, hidden: ctl.flow.hidden, vars: ctl.flow.variables })) return null;
            const id = `${a11y.id}-${i}-${sub.id}`;
            const labelId = `${id}-label`;
            const problem = issueFor(i, sub.id);
            const errId = problem ? `${id}-err` : undefined;
            const descId = sub.description ? `${id}-desc` : undefined;
            const describedBy = [descId, errId].filter(Boolean).join(" ") || undefined;
            const label = (
              <>
                <Markdown text={tpl(sub.label)} />
                {sub.required && <span class="fb-req" aria-hidden="true"> *</span>}
                {sub.required && <span class="fb-sr"> ({t.required})</span>}
              </>
            );
            const control = (
              <div class="fb-control">
                <Control field={sub} value={entry[sub.id]} onChange={(v) => update(i, sub.id, v)} a11y={{ id, labelId, describedBy, invalid: !!problem }} conversational={false} onAdvance={() => {}} />
              </div>
            );
            const desc = sub.description && (
              <div id={descId} class="fb-desc">
                <Markdown text={tpl(sub.description)} />
              </div>
            );
            const err = problem && (
              <p id={errId} class="fb-error">
                {typeof problem.params?.detail === "string" ? problem.params.detail : problem.message}
              </p>
            );
            const cls = `fb-field fb-subfield fb-field--${sub.type}${problem ? " is-invalid" : ""}`;
            return SET_TYPES.has(sub.type) ? (
              <fieldset key={sub.id} class={cls} aria-describedby={describedBy}>
                <legend class="fb-label" id={labelId}>
                  {label}
                </legend>
                {desc}
                {control}
                {err}
              </fieldset>
            ) : (
              <div key={sub.id} class={cls}>
                <label class="fb-label" id={labelId} for={id}>
                  {label}
                </label>
                {desc}
                {control}
                {err}
              </div>
            );
          })}
          {entries.length > Math.max(1, minVisible) && (
            <button type="button" class="fb-linkbtn fb-group-remove" onClick={() => remove(i)}>
              {t.groupRemove(field.itemLabel, i + 1)}
            </button>
          )}
        </fieldset>
      ))}
      {entries.length < field.maxItems && (
        <button type="button" class="fb-btn fb-btn--ghost fb-group-add" onClick={add}>
          + {field.addLabel || t.groupAdd(field.itemLabel)}
        </button>
      )}
      <p class="fb-sr" aria-live="polite">
        {announce}
      </p>
    </div>
  );
}
